import { Tab as MuiTab, Tabs as MuiTabs } from '@mui/material';
import { cn } from 'cn';
import { useMemo } from 'react';

/**
 * Defines a tab configuration for navigation
 * @template TProps - Props passed to the tab content component
 * @property value - Unique identifier for the tab
 * @property label - Display text for the tab
 * @property content - Component to render when tab is active
 * @property views - Array of supported view modes (e.g. ['edit', 'view'])
 */
export type TabDefinition<TProps = unknown> = {
  readonly content: (props: TProps) => React.ReactNode;
  readonly disableCondition?: (props: TProps) => boolean;
  readonly label: string | React.ReactNode;
  readonly value: string;
  readonly views: string[];
};

export type TabsArray<TProps> = ReadonlyArray<TabDefinition<TProps>>;

type TabsProps<TProps = unknown> = {
  readonly contentProps?: TProps;
  readonly onChange: (event: React.SyntheticEvent, value: string) => void;
  readonly tabs: ReadonlyArray<TabDefinition<TProps>>;
  readonly value: string | undefined;
} & ComponentStyling;

export const Tabs = <TProps,>({
  className,
  contentProps,
  onChange,
  style,
  tabs,
  value,
}: TabsProps<TProps>) => {
  const activeTab = useMemo(
    () => tabs.find((tab: TabDefinition<TProps>) => tab.value === value),
    [tabs, value],
  );

  return (
    <>
      <MuiTabs
        className={cn(
          'relative after:absolute after:right-0 after:bottom-0 after:left-0 after:-z-[1] after:h-0.5 after:bg-tiga-border-lighter',
          className,
        )}
        scrollButtons="auto"
        style={style}
        value={value}
        variant="scrollable"
        onChange={onChange}
      >
        {tabs.map((tab: TabDefinition<TProps>) => (
          <MuiTab
            key={tab.value}
            className="relative normal-case transition-colors hover:text-tiga-action-default"
            disabled={tab.disableCondition?.(contentProps as TProps) ?? false}
            label={tab.label}
            value={tab.value}
          />
        ))}
      </MuiTabs>

      {activeTab?.content(contentProps as TProps)}
    </>
  );
};
